import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Handshake, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Surface } from "@/components/ds";

const publicApi = axios.create({ baseURL: API });

const EMPTY = {
  responsavel: "",
  nome_comercio: "",
  ddd: "",
  phone: "",
  cidade: "",
  estado: "",
  instagram: "",
  observacoes: "",
};

export default function PartnerInterest() {
  const [texts, setTexts] = useState({});
  const [studio, setStudio] = useState("TR3ND Studio");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    publicApi
      .get("/public/config")
      .then(({ data }) => {
        setTexts(data.texts || {});
        setStudio(data.studio_name || "TR3ND Studio");
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicApi.post("/public/partner-interest", {
        responsavel: form.responsavel,
        nome_comercio: form.nome_comercio,
        whatsapp: `+55${form.ddd}${form.phone.replace(/\D/g, "")}`,
        cidade: form.cidade,
        estado: form.estado,
        instagram: form.instagram,
        observacoes: form.observacoes,
      });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="partner-interest-page">
      <header className="border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3.5 md:px-8">
          <img
            src="/brand/tr3nd-logo.jpg"
            alt={studio}
            className="h-10 w-10 shrink-0 rounded-lg object-contain"
            data-testid="interest-logo"
          />
          <div className="flex-1">
            <p className="font-display text-base font-semibold leading-none">{studio}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Programa de parceria</p>
          </div>
          <Button asChild variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
            <a href="/catalogo" data-testid="interest-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Catálogo
            </a>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-20 pt-10 md:px-8">
        <div className="max-w-2xl">
          <span className="tr-status tr-status-info">
            <Handshake className="h-3 w-3" /> Parceria comercial
          </span>
          <h1 className="mt-4 text-4xl font-semibold sm:text-5xl" data-testid="interest-title">
            {texts.partner_form_title || "Quero ser parceiro TR3ND"}
          </h1>
          <p className="mt-4 text-base text-muted-foreground" data-testid="interest-text">
            {texts.partner_form_text ||
              "Gostou dos nossos produtos e quer levar a TR3ND para sua loja? Preencha o formulário e entraremos em contato para apresentar como funciona nossa parceria."}
          </p>
        </div>

        <Surface className="mt-8 p-6 md:p-8">
          {sent ? (
            <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="interest-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
              <h2 className="font-display text-lg">Interesse enviado!</h2>
              <p className="max-w-md text-sm text-muted-foreground">
                {texts.partner_form_success ||
                  "Recebemos seu interesse! Nossa equipe entrará em contato em breve para apresentar como funciona a parceria TR3ND."}
              </p>
              <Button asChild variant="outline" className="rounded-full">
                <a href="/catalogo" data-testid="interest-catalog-btn">
                  Voltar ao catálogo
                </a>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Seu nome">
                  <Input
                    value={form.responsavel}
                    onChange={set("responsavel")}
                    required
                    placeholder="Ex.: Maria"
                    data-testid="interest-name-input"
                  />
                </Field>
                <Field label="Nome da loja / empresa">
                  <Input
                    value={form.nome_comercio}
                    onChange={set("nome_comercio")}
                    required
                    placeholder="Ex.: Loja Presentes & Cia"
                    data-testid="interest-store-input"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-[92px_1fr] gap-3">
                <Field label="DDD">
                  <Input
                    value={form.ddd}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, ddd: e.target.value.replace(/\D/g, "").slice(0, 2) }))
                    }
                    required
                    inputMode="numeric"
                    maxLength={2}
                    placeholder="51"
                    className="font-num"
                    data-testid="interest-ddd-input"
                  />
                </Field>
                <Field label="WhatsApp">
                  <Input
                    value={form.phone}
                    onChange={set("phone")}
                    required
                    inputMode="tel"
                    placeholder="99999-9999"
                    className="font-num"
                    data-testid="interest-phone-input"
                  />
                </Field>
              </div>
              <p className="-mt-2 text-[11px] text-muted-foreground">
                Brasil (+55) já está incluído — informe só o DDD e o número.
              </p>

              <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                <Field label="Cidade">
                  <Input value={form.cidade} onChange={set("cidade")} data-testid="interest-city-input" />
                </Field>
                <Field label="Estado">
                  <Input
                    value={form.estado}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, estado: e.target.value.toUpperCase().slice(0, 2) }))
                    }
                    maxLength={2}
                    placeholder="RS"
                    data-testid="interest-state-input"
                  />
                </Field>
              </div>

              <Field label="Instagram da loja">
                <Input
                  value={form.instagram}
                  onChange={set("instagram")}
                  placeholder="@sualoja"
                  data-testid="interest-instagram-input"
                />
              </Field>

              <Field label="Conte um pouco sobre sua loja (opcional)">
                <Textarea
                  value={form.observacoes}
                  onChange={set("observacoes")}
                  rows={3}
                  placeholder="Tipo de público, produtos que já vende, cidade de atuação..."
                  data-testid="interest-about-input"
                />
              </Field>

              <Button
                type="submit"
                disabled={busy}
                className="h-12 rounded-full font-semibold transition-transform active:scale-95"
                data-testid="interest-submit-btn"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Quero conhecer a parceria
              </Button>
              <p className="text-xs text-muted-foreground">
                Sem compromisso e sem documentos agora — é só um primeiro contato comercial.
              </p>
            </form>
          )}
        </Surface>
      </main>
    </div>
  );
}
